import * as React from "react";
import Layout from "../../../components/org/Layout";
import {
    Container,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { flow, pipe } from "fp-ts/function";
import { eventRoute, index, resultsRoute } from "../../../routes/org";
import {
    array,
    either,
    nonEmptyArray,
    ord,
    record,
    separated,
    string,
    task,
    taskEither,
} from "fp-ts";
import { pick, runTask } from "../../../utils/fp";
import { sequenceS } from "fp-ts/Apply";
import { supabase } from "../../../utils/supabase/init";
import { executeSupabaseQuery } from "../../../utils/supabase/useQuery";
import * as t from "io-ts";
import { Subdomain } from "../../../utils/subdomain/subdomain";
import { events } from "../../../domain/event";
import {
    LocalDateString,
    localDateStringFromDateCodec,
} from "../../../domain/common";
import { dmFormat, LocalDate } from "../../../components/common/date/LocalDate";
import { Either } from "fp-ts/Either";
import { contramap, Ord } from "fp-ts/Ord";
import { columns } from "../../../utils/pg-ts/pg-ts";
import { InternalLink } from "../../../components/common/Link";
import CvkLayout from "../../../components/customOrgs/cvk/CvkLayout";

const calendarEventsFields = pipe(
    events.columns,
    pick(
        "id",
        "title",
        "date",
        "date_canceled", // TODO use canceled
        "subtitle",
        "location",
        "results_published"
    )
);
const calendarEventCodec = t.type(calendarEventsFields);
type CalendarEvent = t.TypeOf<typeof calendarEventCodec>;
interface CalendarVisibleEvent extends CalendarEvent {
    calendarDate: LocalDateString;
}
const eventByCalendarDate: Ord<CalendarVisibleEvent> = contramap(
    (event: CalendarVisibleEvent) => event.calendarDate
)(string.Ord);

const calendarEvents = (orgSubdomain: Subdomain) =>
    pipe(
        executeSupabaseQuery(
            "calendarEvents",
            t.array(calendarEventCodec),
            (supabase) =>
                supabase
                    .from("events_published") // TODO pg-ts field/column names
                    .select(columns(calendarEventsFields))
                    .eq("subdomain", orgSubdomain)
                    .order("time_to_start", { ascending: false }),
            supabase
        )
    );

export const getServerSideProps = async (context: GetServerSidePropsContext) =>
    pipe(
        { ...context.params, subdomain: "cvk" },
        index.queryCodec.decode,
        taskEither.fromEither, // TODO learn the fp way to add context
        taskEither.chainW((query) =>
            sequenceS(taskEither.ApplyPar)({
                subdomain: taskEither.right(query.subdomain),
                calendarEvents: calendarEvents(query.subdomain),
            })
        ),
        task.map(
            either.foldW(
                (e) => {
                    console.error("calendar getServerSideProps error", e);
                    return {
                        notFound: true as const,
                    };
                },
                (props) => {
                    return {
                        props,
                    };
                }
            )
        ),
        runTask
    );

export default (p: InferGetServerSidePropsType<typeof getServerSideProps>) => (
    <CvkLayout subdomain={p.subdomain} seo={{ title: "Výsledky" }}>
        <Container maxWidth={"md"}>
            {pipe(
                p.calendarEvents,
                array.map(
                    (event): Either<CalendarEvent, CalendarVisibleEvent> => {
                        const calendarDate = event.date || event.date_canceled; // todo common cancel domain logic?
                        return calendarDate
                            ? either.right({
                                  ...event,
                                  calendarDate,
                                  // TODO domain status: "canceled and rescheduled", "canceled_without_schedule_yet"
                              })
                            : either.left(event);
                    }
                ),
                array.separate,
                separated.mapLeft((l) => {
                    // TODO warns in sentry about event without date
                    return l;
                }),
                separated.right,
                array.map((event) => (
                    <TableRow hover key={event.id}>
                        <TableCell>
                            <LocalDate date={event.calendarDate} />
                        </TableCell>
                        <TableCell>
                            <InternalLink
                                href={eventRoute.route({
                                    subdomain: p.subdomain,
                                    eventId: event.id,
                                })}
                            >
                                <Link
                                    underline={"hover"}
                                    sx={{
                                        fontWeight: "bold",
                                    }}
                                >
                                    {event.title}
                                </Link>
                            </InternalLink>
                        </TableCell>
                        <TableCell>
                            {event.results_published ? (
                                <InternalLink
                                    href={resultsRoute.route({
                                        subdomain: p.subdomain,
                                        eventId: event.id,
                                    })}
                                >
                                    <Link underline={"hover"}>výsledky</Link>
                                </InternalLink>
                            ) : null}
                        </TableCell>
                    </TableRow>
                )),
                (rows) => (
                    <TableContainer sx={{ mb: 4 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Termín</TableCell>
                                    <TableCell>Název</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>{rows}</TableBody>
                        </Table>
                    </TableContainer>
                )
            )}
        </Container>{" "}
    </CvkLayout>
);
